import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import type Locale from 'date-fns';

import {
  FormLabel as MuiFormLabel,
} from '@material-ui/core';

import QuestionForm from '../QuestionForm';
import Memo from '../Memo';
import Box from '../Box';
import { Question } from '../../types/DataTypes';
import { getAnswerUidToAnswerMap } from '../../utils/assessmentUtils';
import { AssessmentResultAnswer, SubAnswer } from '../../types';

const FormLabel = styled(MuiFormLabel)`
  white-space: pre-line;
`;

type Props = {
  name: string;
  question: Question;
  answer: AssessmentResultAnswer;
  onAnswerChange: (questionType: Question, updatedAnswer: AssessmentResultAnswer) => void;
  disabled?: boolean;
  error: boolean;
  fieldRequiredText?: string;
  id?: string;
  locale?: Locale
};

const getQuestionBody = (
  question: Question,
  onAnswerChange: (question: Question, updatedAnswer: AssessmentResultAnswer) => void,
  answer: AssessmentResultAnswer,
  name: string,
  disabled: boolean,
  answerUidToAnswerMap: { [key: string]: SubAnswer},
  locale?: Locale,
) => {
  switch (question.questionType) {
  case 'FREE_FORM_TEXT':
    return (
      <QuestionForm.FreeFormText
        answer={answer}
        question={question}
        onAnswerChange={onAnswerChange}
        disabled={disabled}
      />
    );
  case 'MULTI_SELECT':
    return (
      <QuestionForm.MultipleSelect
        answer={answer}
        question={question}
        answerUidToAnswerMap={answerUidToAnswerMap}
        onAnswerChange={onAnswerChange}
        disabled={disabled}
      />
    );
  case 'MULTIPLE_CHOICE':
    return (
      <QuestionForm.MultipleChoice
        name={name}
        answer={answer}
        question={question}
        onAnswerChange={onAnswerChange}
        answerUidToAnswerMap={answerUidToAnswerMap}
        disabled={disabled}
      />
    );
  case 'DATE_FIELD':
    return (
      <QuestionForm.DateField
        answer={answer}
        question={question}
        onAnswerChange={onAnswerChange}
        disabled={disabled}
        locale={locale}
      />
    );
  default:
    return null;
  }
};

const QuestionDisplay: React.FC<Props> = (props: Props) => {
  const {
    question, onAnswerChange, answer, name, disabled, error, id, fieldRequiredText, locale,
  } = props;

  const [answerUidToAnswerMap, setAnswerUidToAnswerMap] = useState(getAnswerUidToAnswerMap(question.answers));
  useMemo(() => {
    setAnswerUidToAnswerMap(getAnswerUidToAnswerMap(question.answers));
  }, [question.answers]);

  return (
    <Box
      key={question.uid}
      display="block"
      margin="12px"
      width="90%"
      maxWidth="800px"
      id={id}
    >
      <FormLabel error={error}>
        {question.text}
        <span style={{ color: 'red' }}>{question.isOptional ? '' : ' *'}</span>
      </FormLabel>
      <br />
      { error && <span style={{ color: 'red' }}>{fieldRequiredText || 'This field is required.'}</span> }
      { getQuestionBody(question, onAnswerChange, answer, name, disabled, answerUidToAnswerMap, locale) }
    </Box>
  );
};

export default Memo(QuestionDisplay);
